/**
 * Homepage content from the Webflow export (www.nsave.com).
 */

export const homeMeta = {
  title: 'nsave - A Global USD Account',
  description:
    'Open a secure USD account with nsave.com. Enjoy free ACH transfers and send money home to Egypt, Bangladesh, Pakistan & Nigeria, and get your Virtual Mastercard built for freelancers and global remote workers.',
  ogImage: '/images/home/nsave-og.jpg',
  heroHand: {
    src: '/images/home/nsave-hand-1-800.webp',
    mobileSrc: '/images/home/nsave-hand-1-640.webp',
    srcSet:
      '/images/home/nsave-hand-1-480.webp 480w, /images/home/nsave-hand-1-800.webp 800w, /images/home/nsave-hand-1-1200.webp 1200w',
  },
};

export const homeHero = {
  heading: 'Send, receive & invest your money in $, £, €',
  countriesPrefix: 'Serving professionals',
  countriesFrom: 'from:',
  countriesIn: 'in:',
  flags: {
    src: '/images/home/flags-768.webp',
    srcSet: '/images/home/flags-480.webp 480w, /images/home/flags-768.webp 768w',
    width: 768,
    height: 68,
    alt: 'Row of international flags including Switzerland, United Kingdom, Bangladesh, Egypt, Pakistan, Argentina, Turkey, and more',
  },
  nigeriaCallout: {
    label: 'Nigerians',
    flag: { src: '/images/home/nigeria.jpg', alt: 'Flag of Nigeria' },
  },
  mockup: {
    hand: {
      ...homeMeta.heroHand,
      alt: 'Hand holding a phone with the nsave app',
    },
    homeScreen: {
      src: '/images/home/accountmain-320.webp',
      srcSet:
        '/images/home/accountmain-320.webp 320w, /images/home/accountmain-480.webp 480w, /images/home/accountmain-640.webp 640w',
      alt: 'Mobile banking app screen showing a USD account balance with recent transactions',
    },
  },
};

export type HomeFeatureId = 'receive' | 'send' | 'invest';

export const homeFeatures: {
  id: HomeFeatureId;
  title: string;
  href: string;
  videoSrc: string;
  modal: {
    heading: string;
    body: string;
    image: { src: string; alt: string };
    disclaimer: string;
  };
}[] = [
  {
    id: 'receive',
    title: 'Receive in $, £, €',
    href: '/',
    videoSrc: 'https://d2j2t9vgn25o0d.cloudfront.net/videos-v6/receive-usd.mp4',
    modal: {
      heading: 'Receive in $, £, €',
      body: 'A secure, multi-currency, non-interest bearing account that allows you to hold, and exchange USD, EUR, and GBP. Effortlessly transfer funds to others, and receive payments directly into your account — all in one place.',
      image: { src: '/images/home/cash-modal.png', alt: '' },
      disclaimer:
        'GBP and EUR accounts are issued by AF Payments Limited pursuant to a license by Mastercard International. AF Payments Limited is authorised by the Financial Conduct Authority under the Electronic Money Regulations 2011 (FRN: 900440) for the issuing of electronic money and payment instruments. As a registered EMI, AF Payments holds your funds in a safeguarding account which offers protection if AF Payments goes out of business. It is important to know that as a non-bank payment provider, your money is not protected by the Financial Services Compensation Scheme (FSCS). GBP and Multi-Currency accounts are offered by Frost Money Ltd. authorised by the Financial Conduct Authority under the Electronic Money Regulations 2011 (FRN: 936270) for the issuing of electronic money and payment instruments. The Swiss USD accounts are provided by nsave SA a Swiss financial intermediary affiliated to the self-regulatory organisation “Organisme de Surveillance pour Intermédiaires Financiers & Trustees” (SO-FIT). Copyright © 2025 nsave Inc. All rights reserved.',
    },
  },
  {
    id: 'send',
    title: 'Send money home.',
    href: '/send-money-home',
    videoSrc: 'https://d2j2t9vgn25o0d.cloudfront.net/videos-v6/send-money-home.mp4',
    modal: {
      heading: 'Send money home.',
      body: 'Send money to friends and family worldwide with our fast and secure cash payout and pick-up service. Choose from thousands of trusted locations for cash pick-up, making it easy for your recipients to access funds in minutes.',
      image: { src: '/images/home/cash-payouts-modal.webp', alt: '' },
      disclaimer:
        'Local payouts works with GBP and Multi-Currency accounts which are offered by Frost Money Ltd. authorised by the Financial Conduct Authority under the Electronic Money Regulations 2011 (FRN: 936270) for the issuing of electronic money and payment instruments. It is important to know that as a non-bank payment provider, your money is not protected by the Financial Services Compensation Scheme (FSCS). Copyright © 2025 nsave Inc. All rights reserved.',
    },
  },
  {
    id: 'invest',
    title: 'US stocks & ETFs.',
    href: '/invest',
    videoSrc: 'https://d2j2t9vgn25o0d.cloudfront.net/videos-v6/Invest.mp4',
    modal: {
      heading: 'US stocks & ETFs.',
      body: 'An investment feature providing access to a wide range of U.S. stocks, ETFs, bonds, money market funds*, and more – including silver and gold. Designed to help you grow your wealth and achieve your financial goals.',
      image: { src: '/images/home/invest-modal.png', alt: '' },
      disclaimer:
        'Investments involve risks, including the potential total loss of capital. Past performance is not indicative of future results. Each investor is responsible for making their own decisions and should seek advice from a licensed financial advisor or qualified professional if uncertain. Investment and all brokerage & securities services are provided by Alpaca Securities LLC. Alpaca is a FINRA and SEC member. All funds are held by Alpaca. nsave SA, with registered seat at Route de la Galaise 34, 1228 Plan-les-Ouates, Switzerland, is a Swiss financial intermediary affiliated to the self-regulatory organisation “Organisme de Surveillance pour Intermédiaires Financiers & Trustees” (SO-FIT). nsave SA accepts public deposits operating in the framework of the sandbox defined in the Swiss Banking Ordinance. nsave SA does not provide interest bearing accounts and is not supervised by the Swiss financial market supervisory authority FINMA. nsave SA is not a “bank” nor a “person” pursuant to Article 1a or 1b of the Swiss Banking Act and does not benefit from any deposit insurance scheme. nsave SA does not provide any brokerage & securities services. Consult the information sheet here: https://www.nsave.com/terms/informationnotice for more details on financial services provided by nsave SA. Copyright © 2025 nsave Inc. All rights reserved.',
    },
  },
];

export const homePricing = {
  amount: '0.00',
  currency: '$',
  label: 'Per month',
  titleLine1: 'Start free. ',
  titleLine2: 'Grow freely.',
  cta: { label: 'See pricing', href: '/pricing' },
};

export const homeStoryCards = [
  {
    caption: 'Get started',
    title: 'How to open your free nsave account in under 10 minutes',
    href: 'https://intercom.help/nsave_help_center/en/articles/10617199-a-comprehensive-guide-to-opening-your-nsave-account',
    external: true,
    variant: 'red' as const,
  },
  {
    caption: 'Our mission',
    title: 'Enable broad access to the global financial system',
    href: '/about',
    external: false,
    variant: 'space' as const,
    shorter: true,
  },
  {
    caption: 'Careers',
    title: 'Come change the world with us',
    href: '/careers',
    external: false,
    variant: 'careers' as const,
  },
];

export const homeCardCta = {
  titleLine1: 'Earn globally,',
  titleLine2: 'spend locally.',
  cta: { label: 'Download', href: '/get-the-app' },
  image: {
    src: '/images/home/hand-holding-card-800.webp',
    mobileSrc: '/images/home/hand-holding-card-640.webp',
    srcSet:
      '/images/home/hand-holding-card-480.webp 480w, /images/home/hand-holding-card-800.webp 800w, /images/home/hand-holding-card-1200.webp 1200w',
    alt: 'Hand holding nsave card',
  },
};
